import type {Instant, UUID} from './common'
import {formatDateOnly, formatTimestamp} from '@/lib/date-utils'
import i18n from 'i18next'

/* eslint-disable i18next/no-literal-string -- type system definitions */

const InputType = {
    TEXT: 'text',
    NUMBER: 'number',
    DATE: 'date',
    SWITCH: 'switch',
} as const
type InputType = typeof InputType[keyof typeof InputType]

interface ValueTypeDef {
    inputType: InputType
    bodyField: string
    dbColumn: string
    format: (v: unknown) => string
    toBody: (formValue: string) => unknown
}

const _codes = {
    STRING: 'STRING',
    NUMBER: 'NUMBER',
    BOOLEAN: 'BOOLEAN',
    DATE: 'DATE',
    TIMESTAMP: 'TIMESTAMP',
    JSON: 'JSON',
    REFERENCE: 'REFERENCE',
} as const

export type AttributeValueType = typeof _codes[keyof typeof _codes]

const _meta: Record<AttributeValueType, ValueTypeDef> = {
    STRING:    {inputType: InputType.TEXT,   bodyField: 'valueString',   dbColumn: 'value_string',    format: v => String(v),                                                    toBody: v => v},
    NUMBER:    {inputType: InputType.NUMBER, bodyField: 'valueNumber',   dbColumn: 'value_number',    format: v => String(v),                                                    toBody: v => v ? Number(v) : null},
    BOOLEAN:   {inputType: InputType.SWITCH, bodyField: 'valueBoolean',  dbColumn: 'value_boolean',   format: v => v ? i18n.t('common.yes') : i18n.t('common.no'),               toBody: v => v === 'true'},
    DATE:      {inputType: InputType.DATE,   bodyField: 'valueDate',     dbColumn: 'value_date',      format: v => typeof v === 'string' ? formatDateOnly(v) : String(v),        toBody: v => v || null},
    TIMESTAMP: {inputType: InputType.TEXT,   bodyField: 'valueTimestamp', dbColumn: 'value_timestamp', format: v => typeof v === 'string' ? formatTimestamp(v) : String(v),       toBody: v => v || null},
    JSON:      {inputType: InputType.TEXT,   bodyField: 'valueJson',     dbColumn: 'value_json',      format: v => JSON.stringify(v),                                            toBody: v => v ? JSON.parse(v) : null},
    REFERENCE: {inputType: InputType.TEXT,   bodyField: 'valueString',   dbColumn: 'value_string',    format: v => String(v),                                                    toBody: v => v},
}

export const AttributeValueType = {
    ..._codes,
    all: (): AttributeValueType[] => Object.values(_codes),
    inputType: (vt: AttributeValueType): InputType => _meta[vt].inputType,
    bodyField: (vt: AttributeValueType): string => _meta[vt].bodyField,
    dbColumn: (vt: AttributeValueType): string => _meta[vt].dbColumn,
    toBody: (vt: AttributeValueType, formValue: string): unknown => _meta[vt].toBody(formValue),
    format: (value: unknown, vt: AttributeValueType): string =>
        value === null || value === undefined ? '—' : _meta[vt].format(value),
}

/* eslint-enable i18next/no-literal-string */

/**
 * Shared interface for all attribute values across entity types.
 *
 * {@code authorityId} / {@code authorityName} carry the <strong>definition-level</strong>
 * authority — who may write the definition (epic 087). The
 * {@code valueAuthorityTargetSystemId} / {@code valueAuthorityTargetSystemName} fields carry
 * the <strong>row-level</strong> value authority — which target system actually claimed the
 * cell first (Spec §2.3 — multi-source-sync). The two are independent; the UI badge in
 * Spec §6.2 reflects the row-level value authority.
 */
export interface AttributeValue {
    id: UUID
    definitionId: UUID
    name: string
    displayName: string
    valueType: AttributeValueType
    value: unknown
    values?: unknown[]
    authorityId: UUID | null
    authorityName: string | null
    valueAuthorityTargetSystemId: UUID | null
    valueAuthorityTargetSystemName: string | null
    allowedOperations: string[]
    observedAt?: Instant
    createdAt: Instant
    updatedAt: Instant
}

/* eslint-disable i18next/no-literal-string -- field name constants for backend API */
export const AttributeValueResponseFields = {
    id: 'id',
    definitionId: 'definitionId',
    name: 'name',
    displayName: 'displayName',
    valueType: 'valueType',
    value: 'value',
    values: 'values',
    authorityId: 'authorityId',
    authorityName: 'authorityName',
    valueAuthorityTargetSystemId: 'valueAuthorityTargetSystemId',
    valueAuthorityTargetSystemName: 'valueAuthorityTargetSystemName',
    allowedOperations: 'allowedOperations',
    observedAt: 'observedAt',
    createdAt: 'createdAt',
    updatedAt: 'updatedAt',
} as const
/* eslint-enable i18next/no-literal-string */
